import React, { useMemo } from 'react';
import { chain } from 'lodash';
import { isNullOrWhitespace } from 'js/utils/string';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import {
  AssessmentGroup,
  PanelHeader,
  AsyncDisplay,
  LoadingMessage,
  Icon,
  ReplaceLink,
} from 'js/components';
import { Breadcrumbs } from 'js/components/Breadcrumbs';
import {
  Link,
  Switch,
  Route,
  Redirect,
  useRouteMatch,
  matchPath,
} from 'react-router-dom';

import NotFound from 'js/routes/NotFound';
import { isArchived } from 'js/utils/team-utils';
import { Alert } from 'react-bs-notifier';
import styles from './TeamDashboardComponent.module.scss';
import { faClipboardListCheck } from '@fortawesome/pro-light-svg-icons';
import TeamSettings from './TeamSettings';
import { CONNECTOR_SERVICES } from 'js/data/connectors';
import { getConnectorDashboardEntry } from 'js/connectors';
import ConnectorTabContent from './ConnectorTabContent';

const TABS = {
  ACTIVITY: 'activity',
  SETTINGS: 'settings',
};

function getActiveTab(pathname, match) {
  const connectorMatch = matchPath(pathname, {
    path: `${match.path}/connectors/:connectorComponent/:connectorId`,
  });
  if (connectorMatch) {
    return {
      type: 'connector',
      connectorId: connectorMatch.params.connectorId,
    };
  }
  if (
    null !=
    matchPath(pathname, {
      path: `${match.path}/settings`,
    })
  ) {
    return TABS.SETTINGS;
  }
  return TABS.ACTIVITY;
}

const TeamDashboard = (props) => {
  const {
    team = {},
    query = {},
    onAddTeamMember = () => {},
    onUpdateTeamMember = () => {},
    onRemoveTeamMember = () => {},
    assessments = [],
    onToggleAssessmentLock = () => {},
    onDeleteAssessment = () => {},
    onUpdateTeam = () => {},
    onCopyLink = () => {},
    connectors = [],
    connectorsHasLoaded,
    teamConnectorConfigurations = [],
    teamConnectorConfigurationsHasLoaded,
  } = props;

  const match = useRouteMatch();
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';
  const matchedTab = useMemo(
    () => getActiveTab(pathname, match),
    [pathname, match]
  );
  const breadcrumbs = useMemo(
    () => [
      {
        label: 'Teams',
        to: `/w/${team.workspace_id}/teams`,
      },
    ],
    [team.workspace_id]
  );

  const handleUpdateTeam = (values) => {
    return onUpdateTeam({ id: team.id, ...values });
  };

  const hasAssessments = assessments && assessments.length > 0;

  const connectorTabs = useMemo(() => {
    if (
      !teamConnectorConfigurationsHasLoaded ||
      !teamConnectorConfigurations?.length
    )
      return [];
    return teamConnectorConfigurations
      .map((config) => {
        const entry = getConnectorDashboardEntry(config.connector?.type);
        if (!entry || !config.connector) return null;
        const service = CONNECTOR_SERVICES.find(
          (s) => s.id === config.connector.type
        );
        const serviceName = service?.name ?? config.connector.type;
        const connectorName = config.connector.name ?? '';
        const label = connectorName
          ? `${serviceName} – ${connectorName}`
          : serviceName;
        return {
          key: String(config.connector.id),
          label,
          to: `/w/${team.workspace_id}/teams/${team.id}/connectors/${entry.routeSlug}/${config.connector.id}`,
          routeSlug: entry.routeSlug,
        };
      })
      .filter(Boolean);
  }, [
    teamConnectorConfigurations,
    teamConnectorConfigurationsHasLoaded,
    team?.workspace_id,
    team?.id,
  ]);

  const isConnectorTabActive = (tabConnectorId) =>
    matchedTab?.type === 'connector' &&
    String(matchedTab.connectorId) === String(tabConnectorId);

  return (
    <AsyncDisplay
      isLoading={!!query.isLoading && team.id === undefined}
      isError={!!(query.error && query.error.status === 404)}
    >
      <AsyncDisplay.Loading>
        <div>
          <PanelHeader size="sm" />

          <div className="content">
            <Card>
              <CardBody>
                <LoadingMessage iconSize="2x">
                  Loading your team&hellip;
                </LoadingMessage>
              </CardBody>
            </Card>
          </div>
        </div>
      </AsyncDisplay.Loading>
      <AsyncDisplay.Error>
        <NotFound />
      </AsyncDisplay.Error>
      <AsyncDisplay.Resolved>
        <div>
          <PanelHeader size="flex">
            <div className="panel-body-no-label px-4">
              <Breadcrumbs breadcrumbs={breadcrumbs} />
              <div className="content-fluid">
                <div className="row">
                  <div className="col-12 col-md-9">
                    <h1>{team?.name}</h1>
                    {!isNullOrWhitespace(team?.description) && (
                      <p>{team?.description}</p>
                    )}
                  </div>
                  <div className="panel-actions col-12 col-md-3">
                    <Link
                      className="btn btn-primary create-link"
                      to={`/w/${team.workspace_id}/teams/${team.id}/assessments/new`}
                    >
                      <Icon
                        icon={['far', 'poll']}
                        size="lg"
                        className="btn-icon-left"
                      />
                      Assess
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="container-fluid">
              <Nav className="tab-nav nav-underline">
                <NavItem>
                  <NavLink
                    tag={ReplaceLink}
                    active={matchedTab === TABS.ACTIVITY}
                    data-view="active"
                    to={`/w/${team.workspace_id}/teams/${team.id}/dashboard`}
                  >
                    Activity
                  </NavLink>
                </NavItem>
                {connectorTabs.map((tab) => (
                  <NavItem key={tab.key}>
                    <NavLink
                      tag={ReplaceLink}
                      active={isConnectorTabActive(tab.key)}
                      data-view={`connector-${tab.routeSlug}`}
                      to={tab.to}
                    >
                      {tab.label}
                    </NavLink>
                  </NavItem>
                ))}
                <NavItem>
                  <NavLink
                    tag={ReplaceLink}
                    data-view="settings"
                    to={`/w/${team.workspace_id}/teams/${team.id}/settings`}
                    active={matchedTab === TABS.SETTINGS}
                  >
                    Settings
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </PanelHeader>

          <div className="content">
            {team ? (
              <Switch>
                <Route path={`${match.path}/settings`}>
                  <TeamSettings
                    team={team}
                    onUpdate={handleUpdateTeam}
                    onAddTeamMember={onAddTeamMember}
                    onUpdateTeamMember={onUpdateTeamMember}
                    onRemoveTeamMember={onRemoveTeamMember}
                    connectors={connectors}
                    teamConnectorConfigurations={teamConnectorConfigurations}
                    teamConnectorConfigurationsHasLoaded={
                      teamConnectorConfigurationsHasLoaded
                    }
                    connectorsHasLoaded={connectorsHasLoaded}
                  />
                </Route>
                <Route
                  path={`${match.path}/connectors/:connectorComponent/:connectorId`}
                >
                  <ConnectorTabContent
                    team={team}
                    teamConnectorConfigurations={teamConnectorConfigurations}
                    teamConnectorConfigurationsHasLoaded={
                      teamConnectorConfigurationsHasLoaded
                    }
                  />
                </Route>
                <Route exact path={`${match.path}/dashboard`}>
                  <div className={`${styles.TeamInfo}`}>
                    {isArchived(team) && (
                      <Alert color="warning" type="warning" full-width>
                        This team is currently{' '}
                        <span className={styles.ArchiveState}>
                          {team.archived_state?.toUpperCase()}
                        </span>
                        . You will not be able to make edits or create
                        assessments for this team.
                      </Alert>
                    )}

                    <Card>
                      <CardHeader>
                        {hasAssessments && (
                          <div className="action-items">
                            <Link
                              className="btn btn-primary"
                              to={`/w/${team.workspace_id}/teams/${team.id}/assessments/new`}
                              disabled={isArchived(team)}
                            >
                              <Icon
                                icon={['far', 'poll']}
                                size="lg"
                                className="btn-icon-left"
                              />
                              Assess
                            </Link>
                          </div>
                        )}
                        <CardTitle tag="h5">Assessments</CardTitle>
                      </CardHeader>
                      <CardBody>
                        {!hasAssessments ? (
                          <div className="media text-muted">
                            <Icon
                              icon={faClipboardListCheck}
                              size="4x"
                              className="mr-3"
                              fixedWidth
                            />
                            <div className="media-body">
                              <p>
                                You haven't run any assessments for this team
                                yet.
                              </p>
                              <Link
                                className="btn btn-primary"
                                to={`/w/${team.workspace_id}/teams/${team.id}/assessments/new`}
                                disabled={isArchived(team)}
                              >
                                <Icon
                                  icon={['far', 'poll']}
                                  size="lg"
                                  className="btn-icon-left"
                                />
                                Run an assessment now
                              </Link>
                            </div>
                          </div>
                        ) : (
                          chain(assessments)
                            .groupBy('key')
                            .map((assessmentList, key) => {
                              const name = assessmentList[0].name;
                              return (
                                <AssessmentGroup
                                  team={team}
                                  workspaceId={team.workspace_id}
                                  key={key}
                                  templateId={key}
                                  name={name}
                                  assessments={assessmentList}
                                  onToggleAssessmentLock={
                                    onToggleAssessmentLock
                                  }
                                  onDeleteAssessment={onDeleteAssessment}
                                  onCopyLink={onCopyLink}
                                />
                              );
                            })
                            .value()
                        )}
                      </CardBody>
                    </Card>
                  </div>
                </Route>
                <Route path={`${match.path}`}>
                  <Redirect to={`${match.path}/dashboard`} />
                </Route>
              </Switch>
            ) : (
              <Card>
                <CardBody></CardBody>
              </Card>
            )}
          </div>
        </div>
      </AsyncDisplay.Resolved>
    </AsyncDisplay>
  );
};

export default TeamDashboard;
